import React from 'react';
import { motion } from 'framer-motion';
import { PersonStanding, CheckCircle, AlertTriangle, Mail, ExternalLink, FileText } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const AccessibilityStatement: React.FC = () => {
  const { settings } = useSystemSettings();
  const siteUrl = 'https://adoptadinodai.ro';

  const sections = [
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Statutul conformității',
      content: `Acest site este în proces de aliniere la cerințele de accesibilitate prevăzute de O.U.G. nr. 112/2018 și de standardele WCAG 2.2.

Până la finalizarea unei evaluări tehnice complete, declarăm site-ul ca fiind parțial conform. Sunt implementate măsuri de bază pentru navigare, lizibilitate și adaptarea interfeței, iar eventualele neconformități identificate vor fi remediate progresiv.`
    },
    {
      icon: <PersonStanding className="w-6 h-6" />,
      title: 'Funcționalități de accesibilitate disponibile',
      content: `Site-ul include un modul de accesibilitate disponibil din interfața publică. Acesta permite mărirea sau micșorarea textului, activarea contrastului ridicat, afișarea în tonuri de gri, fundal deschis, sublinierea linkurilor, font lizibil și reducerea animațiilor.

Site-ul include și o legătură de tip „Sari la conținut”, vizibilă la navigarea cu tastatura, pentru acces rapid la zona principală a paginii.`
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Conținut posibil neaccesibil',
      content: `Anumite imagini, documente descărcabile sau componente furnizate de servicii externe pot necesita verificări suplimentare pentru conformitate completă.

În cazul în care un document sau o secțiune nu poate fi accesată în mod corespunzător, utilizatorii pot solicita o alternativă accesibilă folosind datele de contact publicate în această pagină.`
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Pregătirea declarației',
      content: `Prezenta declarație a fost pregătită pe baza unei autoevaluări interne a interfeței publice și va fi actualizată după fiecare verificare tehnică relevantă sau după modificări importante ale site-ului.`
    }
  ];

  return (
    <>
      <SEOHead
        title="Declarația privind accesibilitatea"
        description="Declarația privind accesibilitatea pentru site-ul Protecția Animalelor Sector 1, conform O.U.G. nr. 112/2018 și standardelor WCAG."
        keywords="declarație accesibilitate, accesibilitate web, WCAG, OUG 112/2018, persoane cu dizabilități"
        url="/accesibilitate"
        type="website"
        noindex={true}
      />
      <PageHeader
        title="Accesibilitate"
        subtitle="Declarația privind accesibilitatea site-ului și modalități de feedback"
      />

      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-blue-50 border-l-4 border-primary p-6 mb-8 rounded-r-lg">
            <div className="flex items-center mb-3">
              <PersonStanding className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-lg font-bold text-primary">Declarația privind accesibilitatea</h2>
            </div>
            <div className="text-gray-700 space-y-2">
              <p>
                Protecția Animalelor Sector 1 se angajează să asigure accesibilitatea site-ului <strong>{siteUrl}</strong> în conformitate cu O.U.G. nr. 112/2018 privind accesibilitatea site-urilor web și a aplicațiilor mobile ale organismelor din sectorul public.
              </p>
              <p>
                Declarație actualizată la data de <strong>20 mai 2026</strong>.
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-full mr-4 flex-shrink-0">
                    <div className="text-primary">
                      {section.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-primary mb-3 font-display">
                      {section.title}
                    </h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-lg shadow-md p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Feedback și contact
            </h2>
            <p className="text-gray-700 mb-4">
              Dacă întâmpinați dificultăți de accesare a conținutului sau observați o problemă de accesibilitate, ne puteți transmite o solicitare folosind datele de contact de mai jos.
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> {settings?.email || 'protectiaanimalelor@primarias1.ro'}</p>
              <p><strong>Telefon:</strong> {settings?.phone || '+40 750 209 751'}</p>
              <p><strong>Adresa:</strong> {settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</p>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="bg-gray-50 rounded-lg p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Procedura de aplicare</h2>
            <p className="text-gray-700 mb-4">
              În cazul în care considerați că răspunsul primit nu rezolvă problema semnalată, puteți contacta autoritatea competentă de monitorizare:
            </p>
            <a
              href="https://www.adr.gov.ro/en/accesibilitate-site-uri-web"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary font-semibold hover:text-primary-dark underline"
            >
              Autoritatea pentru Digitalizarea României
              <ExternalLink className="w-4 h-4 ml-2" aria-hidden="true" />
            </a>
          </motion.section>
        </motion.div>
      </Container>
    </>
  );
};

export default AccessibilityStatement;
